import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const jsonFiles = [
  { file: 'region.json', description: "Región: ", abbreviation: "re" },
  { file: 'province.json', description: "Provincia: ", abbreviation: "p" },
  { file: 'commune.json', description: "Comuna: ", abbreviation: "co" },
  { file: 'circumscription.json', description: "Circumscripción: ", abbreviation: "ci" },
  { file: 'district.json', description: "Distrito: ", abbreviation: "d" },
  { file: 'sex.json', description: "Sexo: ", abbreviation: "s" },
  { file: 'local.json', description: "Local: ", abbreviation: "t" },
  { file: 'ethnicity.json', description: "Pueblo Indigena: ", abbreviation: "e" },
];

const Filter = ({ onApply }) => {
  const [options, setOptions] = useState({});
  const [selected, setSelected] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [filterDescription, setFilterDescription] = useState('');
  const [onlyPhones, setOnlyPhones] = useState(false);
  const [onlyEmails, setOnlyEmails] = useState(false);
  const [rut, setRut] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    const loadData = async () => {
      const data = {};
      for (const { file } of jsonFiles) {
        try {
          const res = await fetch(`/data-json/${file}`);
          const json = await res.json();
          data[file] = json;
        } catch (err) {
          console.error(`Error loading ${file}:`, err);
        }
      }
      setOptions(data);
    };
    loadData();
  }, []);

  const buildCurrentFilter = () => {
    let filter = jsonFiles.map(({ file, abbreviation }) => {
      const id = selected[file]?._id ?? -1;
      return `${abbreviation}=${id}`;
    }).join('&');

    // extra fields filter
    filter += onlyPhones ? "&op=1" : "&op=-1";
    filter += onlyEmails ? "&oe=1" : "&oe=-1";
    filter += `&i=${encodeURIComponent(rut)}`;
    filter += `&n=${encodeURIComponent(name)}`;

    return filter;
  };

  const buildFilterDescription = () => {
    let description = jsonFiles.map(({ file, description }) => {
      const item = selected[file];
      return item ? `${description}: ${item.key}` : null;
    }).filter(Boolean).join(', ');

    // extra fields filter
    description += onlyPhones ? ", solo usuarios con télefono" : "";
    description += onlyEmails ? ", solo usuarios con email" : "";
    description += rut.trim().length > 0 ? ", rut: " + rut : "";
    description += name.trim().length > 0 ? ", nombre: " + name : "";

    return description;
  };

  const handleChange = (file, value) => {
    setSelected((prev) => ({ ...prev, [file]: value }));
  };

  const handleClear = () => {
    setSelected({});

    setOnlyPhones(false);
    setOnlyEmails(false);
    setRut('');
    setName('');
  };

  const handleApply = () => {
    const current = buildCurrentFilter();
    const desc = buildFilterDescription();
    onApply(current);             // entrega currentFilter a App
    setFilterDescription(desc);   // muestra descripción legible en botón
    setCollapsed(true);           // colapsa el panel
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Panel de filtros */}
      {!collapsed && (
        <AnimatePresence>
          <motion.div
            key="filter-panel"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="p-6 bg-white rounded-2xl shadow-lg space-y-4"
          >
            <h2 className="text-2xl font-semibold text-gray-800">Filtros</h2>
            {jsonFiles.map(({ file, description }) => (
              <div key={file} className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">{description}</label>
                <select
                  className="p-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selected[file]?._id || ''}
                  onChange={(e) => {
                    const selectedId = parseInt(e.target.value);
                    const selectedItem = options[file]?.find(item => item._id === selectedId);
                    handleChange(file, selectedItem || null);
                  }}
                >
                  <option value="">Selecciona una opción</option>
                  {options[file]?.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.key}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-4">
              <span className="text-gray-700">Mostrar solamente ciudadanos con:</span>

              <label className="flex items-center space-x-2 text-gray-800">
                <input
                  type="checkbox"
                  checked={onlyPhones}
                  onChange={() => setOnlyPhones(!onlyPhones)}
                  className="accent-blue-600"
                />
                <span>Teléfono</span>
              </label>

              <label className="flex items-center space-x-2 text-gray-800">
                <input
                  type="checkbox"
                  checked={onlyEmails}
                  onChange={() => setOnlyEmails(!onlyEmails)}
                  className="accent-blue-600"
                />
                <span>Email</span>
              </label>
            </div>

            <div className="flex flex-wrap gap-6">
              {/* Campo RUT */}
              <div className="w-full md:w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rut</label>
                <input
                  type="text"
                  placeholder="Ingrese un RUT"
                  value={rut}
                  onChange={(e) => setRut(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Campo Nombre */}
              <div className="w-full md:w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  placeholder="Ingrese un nombre"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>


            <div className="flex justify-between pt-4">
              <button
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-200 transition"
                onClick={handleClear}
              >
                Limpiar filtros
              </button>

              <button
                className="bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition"
                onClick={handleApply}
              >
                Aplicar filtro
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Botón para mostrar el panel colapsado */}
      {collapsed && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          onClick={() => setCollapsed(false)}
          className="mt-4 block mx-auto px-6 py-2 bg-blue-600 text-white rounded-xl shadow-md hover:bg-blue-700 transition"
        >
          Mostrar filtros {filterDescription && `(${filterDescription})`}
        </motion.button>
      )}
    </div>
  );
};

export default Filter;
